import { authFetch } from './authFetch';
import { admin_apiConfig } from '../config/config';

const catalogUrl = (path) => `${admin_apiConfig.baseURL}${path}`;

const toError = (detail, status) => {
  const message =
    typeof detail === 'string'
      ? detail
      : detail?.message || (Array.isArray(detail) ? JSON.stringify(detail) : String(detail || 'Catalog request failed'));
  const error = { message, status };
  return error;
};

class CatalogQuery {
  constructor(table, schema = 'public') {
    this._table = table;
    this._schema = schema;
    this._operation = 'select';
    this._columns = '*';
    this._filters = [];
    this._orders = [];
    this._payload = null;
    this._limit = null;
    this._offset = null;
    this._rangeEnd = null;
    this._single = false;
    this._maybeSingle = false;
    this._count = null;
    this._head = false;
    this._onConflict = null;
  }

  select(columns = '*', options = {}) {
    if (this._operation === 'select') {
      this._columns = columns ?? '*';
    } else {
      this._columns = columns ?? this._columns;
    }
    if (options?.count) this._count = options.count;
    if (options?.head) this._head = true;
    return this;
  }

  insert(payload) {
    this._operation = 'insert';
    this._payload = payload;
    return this;
  }

  update(payload) {
    this._operation = 'update';
    this._payload = payload;
    return this;
  }

  delete() {
    this._operation = 'delete';
    return this;
  }

  upsert(payload, options = {}) {
    this._operation = 'upsert';
    this._payload = payload;
    this._onConflict = options.onConflict || options.on_conflict || null;
    return this;
  }

  eq(column, value) {
    this._filters.push({ op: 'eq', column, value });
    return this;
  }

  neq(column, value) {
    this._filters.push({ op: 'neq', column, value });
    return this;
  }

  gt(column, value) {
    this._filters.push({ op: 'gt', column, value });
    return this;
  }

  gte(column, value) {
    this._filters.push({ op: 'gte', column, value });
    return this;
  }

  lt(column, value) {
    this._filters.push({ op: 'lt', column, value });
    return this;
  }

  lte(column, value) {
    this._filters.push({ op: 'lte', column, value });
    return this;
  }

  like(column, value) {
    this._filters.push({ op: 'like', column, value });
    return this;
  }

  ilike(column, value) {
    this._filters.push({ op: 'ilike', column, value });
    return this;
  }

  is(column, value) {
    this._filters.push({ op: 'is', column, value });
    return this;
  }

  in(column, values) {
    this._filters.push({ op: 'in', column, value: values });
    return this;
  }

  not(column, operator, value) {
    const op = String(operator || '').toLowerCase();
    if (op === 'is') this._filters.push({ op: 'not_is', column, value });
    else if (op === 'in') this._filters.push({ op: 'not_in', column, value });
    else if (op === 'eq') this._filters.push({ op: 'neq', column, value });
    else throw new Error(`Unsupported catalog NOT operator: ${operator}`);
    return this;
  }

  or(expression) {
    this._filters.push({ op: 'or', value: expression });
    return this;
  }

  order(column, options = {}) {
    const ascending = options.ascending !== false;
    this._orders.push({
      column,
      desc: !ascending,
      ascending,
    });
    return this;
  }

  limit(count) {
    this._limit = count;
    return this;
  }

  range(start, end) {
    this._offset = start;
    this._rangeEnd = end;
    return this;
  }

  single() {
    this._single = true;
    return this;
  }

  maybeSingle() {
    this._maybeSingle = true;
    return this;
  }

  async execute() {
    const response = await authFetch(catalogUrl('/api/configure/catalog/query'), {
      method: 'POST',
      body: JSON.stringify({
        schema_name: this._schema,
        table: this._table,
        operation: this._operation,
        select: this._columns,
        filters: this._filters,
        orders: this._orders,
        limit: this._limit,
        offset: this._offset,
        range_end: this._rangeEnd,
        payload: this._payload,
        single: this._single,
        maybe_single: this._maybeSingle,
        count: this._count,
        head: this._head,
        on_conflict: this._onConflict,
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        data: null,
        error: toError(body.detail || body.error || response.statusText, response.status),
        count: null,
      };
    }
    return {
      data: body.data ?? null,
      error: body.error || null,
      count: body.count ?? null,
    };
  }

  then(onFulfilled, onRejected) {
    return this.execute().then(onFulfilled, onRejected);
  }

  catch(onRejected) {
    return this.execute().catch(onRejected);
  }
}

class CatalogSchema {
  constructor(schema) {
    this._schema = schema;
  }

  from(table) {
    return new CatalogQuery(table, this._schema);
  }
}

export const catalog = {
  from(table) {
    if (typeof table === 'string' && table.includes('.')) {
      const [schema, name] = table.split('.', 2);
      return new CatalogQuery(name, schema);
    }
    return new CatalogQuery(table, 'public');
  },
  schema(schemaName) {
    return new CatalogSchema(schemaName);
  },
  async rpc(functionName, params = {}) {
    const response = await authFetch(catalogUrl('/api/configure/catalog/rpc'), {
      method: 'POST',
      body: JSON.stringify({ function_name: functionName, params }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        data: null,
        error: toError(body.detail || body.error || response.statusText, response.status),
        count: null,
      };
    }
    return { data: body.data ?? null, error: body.error || null, count: body.count ?? null };
  },
};

export const uploadProfilePicture = async ({ file, profileId, workerId }) => {
  const form = new FormData();
  form.append('file', file);
  if (profileId) form.append('profile_id', profileId);
  if (workerId) form.append('worker_id', workerId);
  const response = await authFetch(catalogUrl('/api/configure/account/profile-picture'), {
    method: 'POST',
    body: form,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.detail || 'Failed to upload profile picture');
  }
  return body;
};

export const fetchProfilePictureObjectUrl = async (path) => {
  if (!path) return null;
  const response = await authFetch(
    `${catalogUrl('/api/configure/account/profile-picture')}?path=${encodeURIComponent(path)}`
  );
  if (!response.ok) return null;
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const patchAccountProfile = async (payload) => {
  const response = await authFetch(catalogUrl('/api/configure/account/profile'), {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.detail || 'Failed to update profile');
  }
  return body;
};
